/**
 * Data Room Types
 *
 * Types for the data room checklist and document management system.
 * Based on the Inverio Data Room Checklist methodology.
 */

export type FundingStage = 'pre-seed' | 'seed' | 'series-a' | 'series-b-plus' | 'pre-ipo';

export type DocumentRequirement = 'required' | 'recommended' | 'if-any' | 'if-required' | 'basic' | 'none';

export type DocumentStatus = 'not-started' | 'in-progress' | 'uploaded' | 'verified' | 'not-applicable';

export type ScoringDimension =
  | 'team'
  | 'product'
  | 'market'
  | 'traction'
  | 'defensibility'
  | 'competition'
  | 'business-model'
  | 'timing'
  | 'vision-story'
  | 'gtm-execution'
  | 'financial-health'
  | 'execution-velocity'
  | 'scalability'
  | 'deal-attractiveness'
  | 'social-proof';

export type DocumentCategory =
  | 'corporate-legal'
  | 'financial'
  | 'commercial'
  | 'ip'
  | 'hr-team'
  | 'technology'
  | 'compliance'
  | 'contracts'
  | 'litigation'
  | 'esg-governance';

export interface DataRoomDocument {
  id: string;
  category: DocumentCategory;
  name: string;
  description: string;
  supportsDimensions: ScoringDimension[];
  requirements: {
    'pre-seed': DocumentRequirement;
    seed: DocumentRequirement;
    'series-a': DocumentRequirement;
    'series-b-plus': DocumentRequirement;
    'pre-ipo': DocumentRequirement;
  };
}

export interface DocumentUpload {
  documentId: string;
  status: DocumentStatus;
  uploadedAt?: string;
  verifiedAt?: string;
  fileName?: string;
  fileUrl?: string;
  notes?: string;
}

export interface DataRoomProgress {
  totalDocuments: number;
  requiredDocuments: number;
  uploadedDocuments: number;
  verifiedDocuments: number;
  completionPercentage: number;
  confidenceMultiplier: number;
}

export interface CategoryProgress {
  category: DocumentCategory;
  label: string;
  total: number;
  required: number;
  uploaded: number;
  verified: number;
  percentage: number;
}

export interface DimensionCoverage {
  dimension: ScoringDimension;
  label: string;
  weight: number;
  documentsRequired: number;
  documentsUploaded: number;
  coveragePercentage: number;
}

// Category labels
export const CATEGORY_LABELS: Record<DocumentCategory, string> = {
  'corporate-legal': 'Corporate & Legal',
  financial: 'Financial',
  commercial: 'Commercial',
  ip: 'Intellectual Property',
  'hr-team': 'HR & Team',
  technology: 'Technology',
  compliance: 'Compliance',
  contracts: 'Contracts',
  litigation: 'Litigation',
  'esg-governance': 'ESG & Governance',
};

// Dimension labels
export const DIMENSION_LABELS: Record<ScoringDimension, string> = {
  team: 'Team',
  product: 'Product',
  market: 'Market',
  traction: 'Traction',
  defensibility: 'Defensibility',
  competition: 'Competition',
  'business-model': 'Business Model',
  timing: 'Timing',
  'vision-story': 'Vision/Story',
  'gtm-execution': 'GTM Execution',
  'financial-health': 'Financial Health',
  'execution-velocity': 'Execution Velocity',
  scalability: 'Scalability',
  'deal-attractiveness': 'Deal Attractiveness',
  'social-proof': 'Social Proof',
};

// Stage labels
export const STAGE_LABELS: Record<FundingStage, string> = {
  'pre-seed': 'Pre-Seed',
  seed: 'Seed',
  'series-a': 'Series A',
  'series-b-plus': 'Series B+',
  'pre-ipo': 'Pre-IPO / Late',
};

// Stage-specific dimension weights
export const STAGE_WEIGHTS: Record<FundingStage, Record<ScoringDimension, number>> = {
  'pre-seed': {
    team: 0.12,
    product: 0.09,
    market: 0.07,
    traction: 0.04,
    defensibility: 0.06,
    competition: 0.06,
    'business-model': 0.06,
    timing: 0.07,
    'vision-story': 0.09,
    'gtm-execution': 0.05,
    'financial-health': 0.05,
    'execution-velocity': 0.08,
    scalability: 0.05,
    'deal-attractiveness': 0.05,
    'social-proof': 0.06,
  },
  seed: {
    team: 0.10,
    product: 0.08,
    market: 0.08,
    traction: 0.08,
    defensibility: 0.05,
    competition: 0.05,
    'business-model': 0.06,
    timing: 0.06,
    'vision-story': 0.05,
    'gtm-execution': 0.08,
    'financial-health': 0.07,
    'execution-velocity': 0.06,
    scalability: 0.05,
    'deal-attractiveness': 0.06,
    'social-proof': 0.07,
  },
  'series-a': {
    team: 0.06,
    product: 0.06,
    market: 0.07,
    traction: 0.12,
    defensibility: 0.05,
    competition: 0.06,
    'business-model': 0.07,
    timing: 0.05,
    'vision-story': 0.03,
    'gtm-execution': 0.10,
    'financial-health': 0.10,
    'execution-velocity': 0.07,
    scalability: 0.07,
    'deal-attractiveness': 0.05,
    'social-proof': 0.04,
  },
  'series-b-plus': {
    team: 0.05,
    product: 0.05,
    market: 0.06,
    traction: 0.14,
    defensibility: 0.05,
    competition: 0.06,
    'business-model': 0.08,
    timing: 0.04,
    'vision-story': 0.02,
    'gtm-execution': 0.11,
    'financial-health': 0.11,
    'execution-velocity': 0.07,
    scalability: 0.08,
    'deal-attractiveness': 0.04,
    'social-proof': 0.04,
  },
  'pre-ipo': {
    team: 0.04,
    product: 0.04,
    market: 0.06,
    traction: 0.15,
    defensibility: 0.05,
    competition: 0.06,
    'business-model': 0.09,
    timing: 0.03,
    'vision-story': 0.02,
    'gtm-execution': 0.12,
    'financial-health': 0.12,
    'execution-velocity': 0.06,
    scalability: 0.09,
    'deal-attractiveness': 0.04,
    'social-proof': 0.03,
  },
};

// Confidence scoring based on evidence
export const EVIDENCE_MULTIPLIERS = {
  verified: 1.0, // API/docs verified
  uploaded: 0.95, // Supporting documents uploaded
  'in-progress': 0.90, // Document being prepared
  'self-declared': 0.85, // Founder stated
  missing: 0.70, // No data provided
};
